import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import spinWheelImage from "../../@shared/assets/spin-wheel.png";
import { useAuthStore } from "../../@shared/store/useAuthStore";
import swordsIcon from "../../@shared/assets/icons/swords_icon2.png";
import tokenIcon from "../../@shared/assets/infift_token.png";
import boostIcon from "../../@shared/assets/icons/lightning_icon.png";
import infCardIcon from "../../@shared/assets/back-of-card.png";
import xpIcon from "../../@shared/assets/icons/star_icon.png";

const NUM_SEGMENTS = 10;
const ANGLE_PER_SEGMENT = 360 / NUM_SEGMENTS;

const SpinWheel = ({
  numberSpun,
  setNumberSpun,
  progress,
  values,
  baseAccuracy = 3,
  mode = "attack", // "attack" | "daily"
  rewards, // optional: custom 10 reward items for daily mode
  onReward, // optional: callback(resultReward)
  onSpinStart,
  duration = 1,
}) => {
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [wheelSize, setWheelSize] = useState(200);

  const wheelControls = useAnimation(); // rotation
  const glowControls = useAnimation(); // winner glow pulse
  const containerPulse = useAnimation(); // whole-wheel idle/post pulse

  const { userData } = useAuthStore();

  // effective values to render on the wheel (exactly 10)
  const numbers = useMemo(() => {
    const fallback = Array.from({ length: NUM_SEGMENTS }, (_, i) => i + 1);
    return Array.isArray(values) && values.length === NUM_SEGMENTS
      ? values
      : fallback;
  }, [values]);

  const defaultRewards = useMemo(() => {
    const items = [
      { type: "token", amount: 10, icon: tokenIcon, label: "Infinity Token" },
      { type: "xp", amount: 10, icon: xpIcon, label: "XP" },
      { type: "boost", amount: 1, icon: boostIcon, label: "Boost" },
      { type: "card", amount: 1, icon: infCardIcon, label: "Infinity Card" },
      { type: "token", amount: 5, icon: tokenIcon, label: "Infinity Token" },
      { type: "xp", amount: 20, icon: xpIcon, label: "XP" },
      { type: "token", amount: 15, icon: tokenIcon, label: "Infinity Token" },
      { type: "xp", amount: 10, icon: xpIcon, label: "XP" },
      { type: "card", amount: 1, icon: infCardIcon, label: "Infinity Card" },
      { type: "xp", amount: 30, icon: xpIcon, label: "XP" },
    ];
    return items;
  }, []);

  const holderRewards = useMemo(() => {
    const items = [
      { type: "token", amount: 100, icon: tokenIcon, label: "Infinity Token" },
      { type: "xp", amount: 100, icon: xpIcon, label: "XP" },
      { type: "boost", amount: 2, icon: boostIcon, label: "Boost" },
      { type: "card", amount: 3, icon: infCardIcon, label: "Infinity Card" },
      { type: "token", amount: 50, icon: tokenIcon, label: "Infinity Token" },
      { type: "xp", amount: 50, icon: xpIcon, label: "XP" },
      { type: "token", amount: 75, icon: tokenIcon, label: "Infinity Token" },
      { type: "xp", amount: 50, icon: xpIcon, label: "XP" },
      { type: "card", amount: 2, icon: infCardIcon, label: "Infinity Card" },
      { type: "xp", amount: 75, icon: xpIcon, label: "XP" },
    ];
    return items;
  }, []);

  const rewardSegments = useMemo(() => {
    if (mode !== "daily") return null;
    const list =
      Array.isArray(rewards) && rewards.length === NUM_SEGMENTS
        ? rewards : 
        userData?.orbCount >= 2 ? 
        holderRewards
        : defaultRewards;
    return list;
  }, [mode, rewards, defaultRewards, holderRewards]);

  const accuracyIndexes = useMemo(() => {
    if (mode !== "attack") return [];
    if (typeof baseAccuracy !== "number") return [];
    return numbers.reduce(
      (acc, n, i) => (n === baseAccuracy ? [...acc, i] : acc),
      []
    );
  }, [numbers, baseAccuracy, mode]);

  // responsive size
  useEffect(() => {
    const updateSize = () => setWheelSize(window.innerHeight * 0.35);
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // idle pulse on mount
  useEffect(() => {
    containerPulse.start({
      scale: [1, 1.03, 1],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
    });
  }, [containerPulse]);

  const spinWheel = async () => {
    if (spinning) return;

    // stop idle pulse while spinning
    containerPulse.start({ scale: 1, transition: { duration: 0.2 } });

    // clear old glow
    setSelectedNumber(null);
    glowControls.stop();

    // fast spin: 1.0–1.5s
    const spinDuration = duration + Math.random() * 0.5; // seconds
    const fullSpins = Math.floor(Math.random() * 2) + 6; // 6–7 full turns
    const finalIndex = Math.floor(Math.random() * NUM_SEGMENTS);
    const finalAngle = -(
      (fullSpins * 360 + finalIndex * ANGLE_PER_SEGMENT - 270) // pointer at 12 o'clock, image rotated 90deg
    );

    onSpinStart?.();
    setSpinning(true);

    await wheelControls.start({
      rotate: finalAngle,
      transition: { duration: spinDuration, ease: "linear" },
    });

    setSpinning(false);

    // result
    if (mode === "attack") {
      const result = numbers[finalIndex];
      setSelectedNumber(result);
      setTimeout(() => {
        setNumberSpun?.(result);
      }, 1500);
    } else {
      const reward = rewardSegments[finalIndex];
      setTimeout(() => {
      setSelectedNumber?.(reward); // store object to trigger glow/pulse
      }, 1500);
      // expose to parent (no delay by default; add if you want to sync with SFX)
      onReward?.(reward);
    }

    // restart post-spin gentle pulse
    containerPulse.start({
      scale: [1, 1.03, 1],
      transition: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
    });

    // keep the glow pulsing (doesn't disappear fast)
    glowControls.start({
      scale: [1, 1.15, 1],
      opacity: [0.6, 0.95, 0.6],
      transition: { duration: 0.9, ease: "easeInOut", repeat: Infinity },
    });
  };

  // position helper for labels
  const getCoords = (index) => {
    const angle = ((2 * Math.PI) / NUM_SEGMENTS) * index;
    const radius = wheelSize / 3;
    const x = Math.cos(angle) * radius + wheelSize / 2 - 16;
    const y = Math.sin(angle) * radius + wheelSize / 2 - 16;
    return { left: x, top: y };
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 10,
        position: "relative",
      }}
    >
      {/* pointer */}
      <Box sx={{ position: "absolute", top: 70, zIndex: 100 }}>
        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: "30px solid transparent",
            borderRight: "30px solid transparent",
            borderTop: "30px solid #ff4757",
            borderTopRightRadius: 10,
            borderTopLeftRadius: 10,
            borderBottomRadius: 10,
          }}
        />
      </Box>

      {/* tutorial hit area (unchanged) */}
      {!userData?.completedTutorial && progress?.currentStepIndex === 10 && (
        <Box
          data-driver="spin-wheel"
          style={{ overflow: "visible" }}
          sx={{
            position: "absolute",
            top: { xs: "70px", md: "70px" },
            left: { xs: "60px", md: "302px" },
            width: { xs: "255px", md: "275px" },
            height: { xs: "245px", md: "165px" },
            zIndex: 0,
            backgroundColor: "transparent",
            pointerEvents: "none",
          }}
          onClick={spinWheel}
        />
      )}

      {/* pulsing wrapper */}
      <motion.div
        animate={containerPulse}
        style={{ position: "relative", width: wheelSize, height: wheelSize }}
      >
        <motion.div
          style={{
            width: wheelSize,
            height: wheelSize,
            borderRadius: wheelSize / 2,
            // border: "6px solid #2d3436",
            overflow: "hidden",
            position: "absolute",
          }}
          animate={wheelControls}
        >
          <Box
            component="img"
            src={spinWheelImage}
            alt="Spin Wheel"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: wheelSize / 2,
              transform: "rotate(90deg)",
            }}
          />
          {/* 
          {numbers.map((num, index) => {
            const { left, top } = getCoords(index);
            const rotation = ANGLE_PER_SEGMENT * index + 90;
            const isAccuracy = accuracyIndexes.includes(index);

            return (
              <Box
                key={`num-${index}`}
                sx={{
                  position: "absolute",
                  width: 30,
                  height: 30,
                  left,
                  top,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  transform: `rotate(${rotation}deg)`,
                }}
              >
                {isAccuracy && (
                  <Box
                    component="img"
                    src={swordsIcon}
                    alt="accuracy"
                    sx={{
                      position: "absolute",
                      width: 60,
                      height: 60,
                      opacity: 0.85, // tweak if too bright
                      zIndex: 0,
                      pointerEvents: "none",
                      filter: "drop-shadow(0 0 4px rgba(255,229,82,0.7))",
                    }}
                  />
                )}

                <Typography
                  sx={{
                    fontSize: num === numberSpun ? 36:18,
                    fontWeight: "bold",
                    color: "#000",
                    textShadow: `-1px -1px 0 #fff, 1px -1px 0 #fff, -1px  1px 0 #fff, 1px  1px 0 #fff`,
                    zIndex: 1,
                  }}
                >
                  {num}
                </Typography>
              </Box>
            );
          })} */}
          {/* SEGMENTS */}
          {mode === "attack" &&
            numbers?.map((num, index) => {
              const { left, top } = getCoords(index);
              const rotation = ANGLE_PER_SEGMENT * index + 90;
              const isAccuracy = accuracyIndexes.includes(index);

              return (
                <Box
                  key={`attack-${index}`}
                  sx={{
                    position: "absolute",
                    width: 30,
                    height: 30,
                    left,
                    top,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: `rotate(${rotation}deg)`,
                  }}
                >
                  {isAccuracy && (
                    <Box
                      component="img"
                      src={swordsIcon}
                      alt="accuracy"
                      sx={{
                        position: "absolute",
                        width: 60,
                        height: 60,
                        opacity: 0.85,
                        zIndex: 0,
                        pointerEvents: "none",
                        filter: "drop-shadow(0 0 4px rgba(255,229,82,0.7))",
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      fontSize: num === numberSpun ? 36 : 18,
                      fontWeight: "bold",
                      color: "#000",
                      textShadow: `-1px -1px 0 #fff, 1px -1px 0 #fff, -1px  1px 0 #fff, 1px  1px 0 #fff`,
                      zIndex: 1,
                    }}
                  >
                    {num}
                  </Typography>
                </Box>
              );
            })}

          {mode === "daily" &&
            rewardSegments?.map((r, index) => {
              const { left, top } = getCoords(index);
              const rotation = ANGLE_PER_SEGMENT * index + 90;
              return (
                <Box
                  key={`daily-${index}`}
                  sx={{
                    position: "absolute",
                    width: 46,
                    height: 46,
                    left: left - 8, // slightly larger footprint for icon+text
                    top: top - 8,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    transform: `rotate(${rotation}deg)`,
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    component="img"
                    src={r.icon}
                    alt={r.label}
                    sx={{
                      position: "absolute",
                      width: 34,
                      height: r.type === 'card' ? 48 : 34,
                      zIndex: 1,
                      filter: "drop-shadow(0 0 6px rgba(0,0,0,0.35))",
                    }}
                  />
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 24,
                      fontSize: 14,
                      fontWeight: 800,
                      color: "#111",
                      textShadow: `-1px -1px 0 #fff, 1px -1px 0 #fff, -1px  1px 0 #fff, 1px  1px 0 #fff`,
                      zIndex: 2,
                    }}
                  >
                    {r.type === "xp" ?`${r.amount + r.type.toUpperCase()}` : r.amount}
                  </Typography>
                </Box>
              );
            })}
        </motion.div>

        {/* winner glow (stays pulsing after result) */}
        {selectedNumber !== null && !spinning && (
          <motion.div
            animate={glowControls}
            style={{
              position: "absolute",
              width: 64,
              height: 64,
              borderRadius: 999,
              border: "2px solid #FFD700",
              // FIXED: valid multiple box-shadows for strong halo
              boxShadow:
                "0 0 10px #FFE552, 0 0 18px #FFE552, 0 0 28px rgba(255,229,82,0.8)",
              zIndex: 5, // above the wheel graphic
              left: wheelSize / 2 - 32, // center horizontally (64/2)
              top: 18, // tweak to sit right under the pointer triangle
              pointerEvents: "none",
              // Optional: makes glow pop on dark backgrounds
              mixBlendMode: "screen",
            }}
          />
        )}

        {/* start button */}
        <Button
          onClick={spinWheel}
          disabled={spinning}
          variant="contained"
          sx={{
            position: "absolute",
            left: "52%",
            top: "52%",
            transform: "translate(-50%, -50%)",
            width: 100,
            height: 100,
            borderRadius: "50%",
            backgroundColor: "#ff4757",
            color: "#fff",
            zIndex: 10,
            boxShadow: "0px 2px 4px rgba(0,0,0,0.3)",
          }}
        >
          {spinning ? "..." : "START"}
        </Button>
      </motion.div>
    </Box>
  );
};

export default SpinWheel;
